import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWebsites, deleteWebsite } from '../../lib/supabase/websites';
import { supabase } from '../../lib/supabase';
import { Website } from '../../types/website';
import { faArrowUpRightFromSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button, Badge, Icon, KebabMenu, Table, PulseDot } from '../../components/ui';
import type { TableColumn } from '../../components/ui';
import AddWebsiteSidepanel from './AddWebsiteSidepanel';

interface WebsiteTableProps {
  onWebsitesUpdated: () => void;
  searchTerm?: string;
  selectedColor?: string;
  onColorFilterChange?: (color: string) => void;
  forceRefresh?: number;
}

type SortField = 'url' | 'title' | 'category' | 'lastContentUpdate';

export default function WebsiteTable({
  onWebsitesUpdated,
  searchTerm = '',
  selectedColor = '',
  onColorFilterChange,
  forceRefresh,
}: WebsiteTableProps) {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddSidepanelOpen, setIsAddSidepanelOpen] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    field: SortField | null;
    order: 'asc' | 'desc' | null;
  }>({ field: null, order: null });
  const [isExporting, setIsExporting] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Fetch all websites (not just filtered ones) for complete export
      const { data: allWebsites, error: exportError } = await supabase
        .from('websites')
        .select('id, url, title, created_at, color, build_status, deployment_status, dns_host, hosting_provider, hosting_username, frontend_platform, frontend_theme, search_console_access, indexing_status, nameservers, frontend_username, registrar, registrar_username, hosting_location, ip_address, last_content_update, category, domain')
        .order('created_at', { ascending: false });

      if (exportError) throw exportError;

      if (!allWebsites || allWebsites.length === 0) {
        setError('No websites to export');
        return;
      }

      // Define CSV headers
      const headers = [
        'URL',
        'Title',
        'Category',
        'Color',
        'Build Status',
        'Deployment Status',
        'Indexing Status',
        'DNS Host',
        'Registrar',
        'Hosting Provider',
        'Hosting Location',
        'IP Address',
        'Frontend Platform',
        'Frontend Theme',
        'Search Console Access',
        'Last Content Update',
        'Created At'
      ];

      // Convert data to CSV format
      const csvContent = [
        headers.join(','),
        ...allWebsites.map(website => [
          `"${website.url || ''}"`,
          `"${website.title || ''}"`,
          `"${website.category || ''}"`,
          `"${website.color || ''}"`,
          `"${website.build_status || ''}"`,
          `"${website.deployment_status || ''}"`,
          `"${website.indexing_status || ''}"`,
          `"${website.dns_host || ''}"`,
          `"${website.registrar || ''}"`,
          `"${website.hosting_provider || ''}"`,
          `"${website.hosting_location || ''}"`,
          `"${website.ip_address || ''}"`,
          `"${website.frontend_platform || ''}"`,
          `"${website.frontend_theme || ''}"`,
          `"${website.search_console_access || ''}"`,
          `"${website.last_content_update ? formatDate(website.last_content_update) : ''}"`,
          `"${website.created_at ? formatDate(website.created_at) : ''}"`
        ].join(','))
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `websites-export-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error exporting websites:', err);
      setError('Failed to export websites. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const loadWebsites = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchWebsites();
      setWebsites(data);
    } catch (err) {
      console.error('Error loading websites:', err);
      setError('Failed to load websites. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites, forceRefresh]);

  const handleDeleteWebsite = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this website? This will also remove all its connections.')) {
      setDeletingId(id);
      try {
        await deleteWebsite(id);
        setWebsites(prev => prev.filter(website => website.id !== id));
        onWebsitesUpdated();
      } catch (err) {
        console.error('Error deleting website:', err);
        setError('Failed to delete website. Please try again.');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prev => {
      if (prev.field !== field) return { field, order: 'asc' };
      if (prev.order === 'asc') return { field, order: 'desc' };
      if (prev.order === 'desc') return { field: null, order: null };
      return { field, order: 'asc' };
    });
  };

  const formatUrl = (url: string): string => {
    // Remove protocol and trailing slash for display
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  };

  // Get unique colors from websites
  const uniqueColors = useMemo(() => {
    const colors = websites
      .map(w => w.color)
      .filter((color): color is string => !!color);
    return Array.from(new Set(colors)).sort();
  }, [websites]);

  const filteredWebsites = websites.filter(website => {
    const matchesSearch = website.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (website.title && website.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (website.category && website.category.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesColor = !selectedColor || website.color === selectedColor;

    return matchesSearch && matchesColor;
  });

  const sortedWebsites = useMemo(() => {
    if (!sortConfig.field) return filteredWebsites;
    const sorted = [...filteredWebsites].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortConfig.field) {
        case 'url':
          aVal = a.url.toLowerCase();
          bVal = b.url.toLowerCase();
          break;
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'category':
          aVal = (a.category || '').toLowerCase();
          bVal = (b.category || '').toLowerCase();
          break;
        case 'lastContentUpdate':
          aVal = new Date(a.last_content_update || a.created_at || 0).getTime();
          bVal = new Date(b.last_content_update || b.created_at || 0).getTime();
          break;
        default:
          break;
      }
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortConfig.order === 'asc' ? sorted : sorted.reverse();
  }, [filteredWebsites, sortConfig]);

  const handleWebsiteClick = (websiteId: string) => {
    navigate(`/private-network/${websiteId}`);
  };

  // Sortable column header — keeps the eyebrow type from the table header
  const sortableHeader = (label: string, field: SortField) => (
    <button
      onClick={() => handleSort(field)}
      className="pressable"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
        letterSpacing: 'inherit',
        textTransform: 'inherit',
        color: sortConfig.field === field ? 'var(--text-muted)' : 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
      }}
    >
      {label}
      <Icon
        name="chevron-down"
        size={11}
        style={{
          opacity: sortConfig.field === field ? 1 : 0.35,
          transform: sortConfig.field === field && sortConfig.order === 'desc' ? 'rotate(180deg)' : 'none',
          transition: 'transform var(--dur-base) var(--ease-out)',
        }}
      />
    </button>
  );

  const isIncomplete = (website: Website) =>
    Object.values(website as unknown as Record<string, unknown>).some(v => v === null);

  // Shared by the table's last column and the phone rows.
  const rowActions = (row: Website) => (
    deletingId === row.id ? (
      <span
        className="inline-block w-4 h-4 rounded-full animate-spin"
        style={{ border: '2px solid var(--border-hairline)', borderTopColor: 'var(--accent)' }}
      />
    ) : (
      <KebabMenu
        label="Site actions"
        actions={[
          {
            label: 'Open Site',
            icon: faArrowUpRightFromSquare,
            onClick: () => {
              window.open(
                row.url.match(/^https?:\/\//) ? row.url : `https://${row.url}`,
                '_blank',
                'noopener,noreferrer'
              );
            },
          },
          {
            label: 'Delete',
            icon: faTrash,
            danger: true,
            onClick: () => handleDeleteWebsite(row.id),
          },
        ]}
      />
    )
  );

  const columns: TableColumn<Website>[] = [
    {
      key: 'url',
      label: sortableHeader('Domain', 'url'),
      mono: true,
      render: (value: string, row: Website) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
          <span
            style={{
              width: '9px',
              height: '9px',
              borderRadius: 'var(--radius-pill)',
              background: row.color || '#d95636',
              flexShrink: 0,
            }}
            title={row.color ? `Color: ${row.color}` : undefined}
          />
          <span style={{ fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>
            {formatUrl(value)}
          </span>
          {isIncomplete(row) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <PulseDot tone="down" title="Incomplete record" />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)', fontFamily: 'var(--font-ui)' }}>
                Incomplete
              </span>
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'title',
      label: sortableHeader('Title', 'title'),
      render: (value: string | null) => (
        <span style={{ fontSize: 'var(--text-sm)', color: value ? 'var(--text-body)' : 'var(--text-faint)' }}>
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'category',
      label: sortableHeader('Category', 'category'),
      render: (value: string | null) =>
        value ? <Badge tone="neutral">{value}</Badge> : <span style={{ color: 'var(--text-faint)' }}>-</span>,
    },
    {
      key: 'last_content_update',
      label: sortableHeader('Last Update', 'lastContentUpdate'),
      mono: true,
      render: (value: string | null, row: Website) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {value ? formatDate(value) : row.created_at ? formatDate(row.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      align: 'right',
      render: (_value: string, row: Website) => rowActions(row),
    },
  ];

  return (
    <div className="w-full">
      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ flex: 1 }}>{error}</span>
          <Button size="sm" variant="ghost" onClick={loadWebsites} style={{ color: 'inherit' }}>
            Try Again
          </Button>
        </div>
      )}

      {/* Color Filter */}
      {uniqueColors.length > 0 && onColorFilterChange && (
        <div
          className="flex items-center flex-wrap"
          style={{ gap: '8px', marginBottom: 'var(--space-4)' }}
        >
          <span className="t-eyebrow">Color</span>
          <button
            onClick={() => onColorFilterChange('')}
            // Taller on phones: 24px chips are a hard tap
            className="pressable hover-wash h-8 md:h-6"
            style={{
              padding: '0 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid ' + (!selectedColor ? 'var(--border-strong)' : 'var(--border-hairline)'),
              background: !selectedColor ? 'var(--surface-active)' : 'transparent',
              color: !selectedColor ? 'var(--text-body)' : 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-xs)',
              cursor: 'pointer',
            }}
          >
            All
          </button>
          {uniqueColors.map((color) => (
            <button
              key={color}
              onClick={() => onColorFilterChange(color)}
              className="pressable hover-wash h-8 md:h-6"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '0 10px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid ' + (selectedColor === color ? 'var(--border-strong)' : 'var(--border-hairline)'),
                background: selectedColor === color ? 'var(--surface-active)' : 'transparent',
                color: selectedColor === color ? 'var(--text-body)' : 'var(--text-muted)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-2xs)',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: '9px', height: '9px', borderRadius: 'var(--radius-pill)', background: color }} />
              {color}
            </button>
          ))}
        </div>
      )}

      {/* Table Content */}
      {isLoading ? (
        <div
          className="text-center"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-10)',
          }}
        >
          <div
            className="w-8 h-8 rounded-full animate-spin mx-auto"
            style={{
              border: '3px solid var(--border-hairline)',
              borderTopColor: 'var(--accent)',
              marginBottom: 'var(--space-4)',
            }}
          />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading websites...</p>
        </div>
      ) : filteredWebsites.length === 0 ? (
        <div
          className="text-center"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-10)',
          }}
        >
          <div style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}>
            <Icon name="globe" size={28} />
          </div>
          <h3
            style={{
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
              marginBottom: 'var(--space-2)',
            }}
          >
            No websites found
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-4)' }}>
            {searchTerm ? 'Try a different search term' : 'Add your first website to get started'}
          </p>
          {!searchTerm && (
            <div className="flex justify-center" style={{ gap: '10px' }}>
              <Button size="sm" icon="plus" onClick={() => setIsAddSidepanelOpen(true)}>
                Add Website
              </Button>
              <Button size="sm" variant="secondary" onClick={handleExportCSV} disabled={isExporting}>
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Phones: one row per site with title, category and last update
              under the domain. The table clips everything past Domain. */}
          <div
            className="md:hidden"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-rest)',
              overflow: 'hidden',
            }}
          >
            {sortedWebsites.map((row, i) => {
              const updated = row.last_content_update || row.created_at;
              return (
                <div
                  key={row.id}
                  role="link"
                  tabIndex={0}
                  className="hover-wash"
                  onClick={() => handleWebsiteClick(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleWebsiteClick(row.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '11px 8px 11px 14px',
                    borderTop: i ? '1px solid var(--border-hairline)' : 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{ width: '9px', height: '9px', borderRadius: 'var(--radius-pill)', background: row.color || '#d95636', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 8px' }}>
                      <span
                        className="t-num"
                        style={{ fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)', overflowWrap: 'anywhere' }}
                      >
                        {formatUrl(row.url)}
                      </span>
                      {isIncomplete(row) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>
                          <PulseDot tone="down" title="Incomplete record" />
                          Incomplete
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[row.title, row.category, updated ? formatDate(updated) : null].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  {rowActions(row)}
                </div>
              );
            })}
          </div>
          <div className="hidden md:block">
            <Table<Website>
              columns={columns}
              rows={sortedWebsites}
              onRowClick={(row) => handleWebsiteClick(row.id)}
            />
          </div>
        </>
      )}

      {/* Add Website Sidepanel */}
      <AddWebsiteSidepanel
        isOpen={isAddSidepanelOpen}
        onClose={() => setIsAddSidepanelOpen(false)}
        onWebsiteAdded={loadWebsites}
      />
    </div>
  );
}
